import React from "react";
import { useTranslation } from "react-i18next";
import { useFunnelNavigate } from "@/hooks/useFunnelNavigate";
import { createOffer, createApplication, uploadDocument, getOffer } from "@/api/simpego";
import { useErrorDialog } from "@/context/ErrorDialogContext";
import { useAgentId } from "@/context/AgentIdContext";
import { buildDispatchControl, OfferDispatchSnapshot, DispatchRecipient } from "@/utils/dispatchControl";
import { readPathParam } from "@/utils/urlParams";
import { Customer, ContactDetails, Address, OfferRequest, OfferResponse, ApplicationRequest, PreexistingConditions, PremiumCorrection } from "@/types/simpego-api";
import { ProductVariant } from "@/hooks/useFunnelState";
import { BaseCoverageId } from "@/config/coverages";
import { CoverageState, buildCoveragesPayload, buildContractCoveragesPayload } from "@/utils/coveragePayload";
import { format } from "date-fns";
import * as Sentry from "@sentry/react";
import { reportUnderwritingWarning } from "@/utils/underwritingWarning";
import { getCustomerErrorMessage, isHandledValidationError } from "@/utils/apiError";

const DEFAULT_AGENT_ID = typeof window !== 'undefined' && window.__env__?.defaultAgentId ? window.__env__.defaultAgentId : "VA0000000003";


interface UseFunnelActionsProps {
  petType: "cat" | "dog" | null;
  petName: string | null;
  breed: string | null;
  birthDate: Date | undefined;
  selectedBreedType: "purebred" | "crossbreed" | "mixed" | null;
  gender: "male" | "female" | "unknown" | null;
  isCastrated: boolean;
  isHouseCat: boolean;
  selectedDogSize: "small" | "large" | null;
  chipNumber: string | null;
  preexistingConditionCategories: string[];
  preexistingConditionComment: string | null;
  selectedProduct: ProductVariant | null;
  hasSelectedPlan: boolean;
  bundleRecommendations: { S: OfferResponse; M: OfferResponse; L: OfferResponse } | null;
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  subCoverageSelections: Partial<Record<BaseCoverageId, Record<string, boolean>>>;
  subCoverageSumInsured: Partial<Record<BaseCoverageId, Record<string, number>>>;
  coverageDeductibles: Partial<Record<BaseCoverageId, number>>;
  coverageDeductiblesOtherBand: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsured: Partial<Record<BaseCoverageId, number>>;
  coverageSumInsuredPerCase: Partial<Record<BaseCoverageId, string>>;
  emergencyCareSumInsuredPerDay: "50" | "100" | null;
  annualDeductible: number | null;
  dailyTermination: boolean;
  paymentInterval: 1 | 2 | 4 | 12;
  paymentMethod: "credit_card" | "ESR";
  startOfInsurance: Date;
  firstDue: Date | null;
  contractId: string | null;
  contractToken: string | null;
  agentId: string | null;
  whitelabelPartner: string | null;
  offerDispatchSnapshot: OfferDispatchSnapshot | null;
  premiumCorrections: PremiumCorrection[] | null;
  dispatchContract: DispatchRecipient;
  dispatchInvoice: DispatchRecipient;
  dispatchReminder: DispatchRecipient;
  dispatchEmail: DispatchRecipient;
  agentDispatchEmail: string | null;
  campaignCode: string;
  contactBirthdate: Date | undefined;
  customerGender: "male" | "female" | null;
  contactFirstName: string | null; // New prop
  contactLastName: string | null;  // New prop
  offerLoadedFromUrl: boolean;
  hasLeftConfirmationAfterOfferLoad: boolean;
  consultationProtocolDocumentId: number | null;
  consultationProtocolPendingFile: string | null;
  consultationProtocolPendingFilename: string | null;
  setConsultationProtocolDocument: (documentId: number, filename: string) => void;
  clearConsultationProtocolDocument: () => void;
  clearConsultationProtocolPending: () => void;
  handleResetFunnel: () => void;
  handlePetDetailsSubmitState: (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedCoverageSelections: Partial<Record<BaseCoverageId, boolean>>,
    submittedStartOfInsurance: Date,
    submittedChipNumber: string | null
  ) => void;
  setBundlePrices: (prices: { S: number; M: number; L: number } | null) => void;
  setBundleRecommendations: (data: { S: OfferResponse; M: OfferResponse; L: OfferResponse } | null) => void;
  applyRecommendation: (product: ProductVariant, recommendation: OfferResponse) => void;
  setFinalPrice: (price: number | null) => void;
  setConfirmedOffer: (offer: OfferResponse | null) => void;
  setContractId: (id: string | null) => void;
  setContractToken: (token: string | null) => void;
  setOfferLoadedFromUrl: (value: boolean) => void;
  setHasLeftConfirmationAfterOfferLoad: (value: boolean) => void;
  handleContactDetailsSubmitState: (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    city: string,
    zip: string,
    country: string,
    birthdate: Date | undefined,
    gender: "male" | "female" | ""
  ) => void;
  fetchBundleRecommendations: (
    petTypeArg: "cat" | "dog",
    petNameArg: string,
    breedArg: string | null,
    birthDateArg: Date | undefined,
    selectedBreedTypeArg: "purebred" | "crossbreed" | "mixed",
    genderArg: "male" | "female" | "unknown",
    isCastratedArg: boolean,
    isHouseCatArg: boolean,
    selectedDogSizeArg: "small" | "large" | null,
    coverageSelectionsArg: Partial<Record<BaseCoverageId, boolean>>,
    locale: string,
    agentIdArg: string | null,
    startOfInsuranceArg: Date
  ) => Promise<{ S: OfferResponse; M: OfferResponse; L: OfferResponse } | null>;
  underwritingFailed: boolean;
  underwritingReason: string | null;
  currentLocale: string;
}

export const useFunnelActions = ({
  petType,
  petName,
  breed,
  birthDate,
  selectedBreedType,
  gender,
  isCastrated,
  isHouseCat,
  selectedDogSize,
  chipNumber,
  preexistingConditionCategories,
  preexistingConditionComment,
  selectedProduct,
  hasSelectedPlan,
  bundleRecommendations,
  coverageSelections,
  subCoverageSelections,
  subCoverageSumInsured,
  coverageDeductibles,
  coverageDeductiblesOtherBand,
  coverageSumInsured,
  coverageSumInsuredPerCase,
  emergencyCareSumInsuredPerDay,
  annualDeductible,
  dailyTermination,
  paymentInterval,
  paymentMethod,
  startOfInsurance,
  firstDue,
  contractId,
  contractToken,
  agentId,
  whitelabelPartner,
  offerDispatchSnapshot,
  premiumCorrections,
  dispatchContract,
  dispatchInvoice,
  dispatchReminder,
  dispatchEmail,
  agentDispatchEmail,
  campaignCode,
  contactBirthdate,
  customerGender,
  contactFirstName, // Destructure new prop
  contactLastName,  // Destructure new prop
  offerLoadedFromUrl,
  hasLeftConfirmationAfterOfferLoad,
  consultationProtocolDocumentId,
  consultationProtocolPendingFile,
  consultationProtocolPendingFilename,
  setConsultationProtocolDocument,
  clearConsultationProtocolDocument,
  clearConsultationProtocolPending,
  handleResetFunnel,
  handlePetDetailsSubmitState,
  setBundlePrices,
  setBundleRecommendations,
  applyRecommendation,
  setFinalPrice,
  setConfirmedOffer,
  setContractId,
  setContractToken,
  setOfferLoadedFromUrl,
  setHasLeftConfirmationAfterOfferLoad,
  handleContactDetailsSubmitState,
  fetchBundleRecommendations,
  underwritingFailed,
  underwritingReason,
  currentLocale,
}: UseFunnelActionsProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useFunnelNavigate();
  const { showErrorDialog } = useErrorDialog();
  const { agentToken } = useAgentId();
  const [isSubmittingOffer, setIsSubmittingOffer] = React.useState<boolean>(false);

  const getLocalizedPath = React.useCallback((pathKey: string) => {
    const baseRoute = i18n.getResource(currentLocale, 'translation', pathKey) as string;
    return `/${currentLocale}${baseRoute.startsWith('/') ? baseRoute : `/${baseRoute}`}`;
  }, [currentLocale, i18n]);

  const buildOfferPayload = (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    submittedCity: string,
    submittedZip: string,
    submittedCountry: string,
    customerBirthdateOverride?: Date,
    customerGenderOverride?: "male" | "female" | ""
  ): OfferRequest | null => {
    const effectiveCustomerBirthdate = customerBirthdateOverride ?? contactBirthdate;
    const effectiveCustomerGender = customerGenderOverride !== undefined ? customerGenderOverride : customerGender;
    if (!petType || !petName || !birthDate || !selectedBreedType || !gender || !hasSelectedPlan) {
      showErrorDialog(t("common.errorCalculatingPrice"));
      return null;
    }
    if ((selectedBreedType === "purebred" || selectedBreedType === "crossbreed") && !breed) {
      showErrorDialog(t("petDetails.validation.selectBreed"));
      return null;
    }
    if (selectedBreedType === "mixed" && petType === "dog" && selectedDogSize === null) {
      showErrorDialog(t("petDetails.validation.selectDogSize"));
      return null;
    }

    const formattedStartOfInsurance = format(startOfInsurance, "yyyy-MM-dd");
    const formattedPetBirthdate = format(birthDate, "yyyy-MM-dd");
    const formattedCustomerBirthdate = effectiveCustomerBirthdate ? format(effectiveCustomerBirthdate, "yyyy-MM-dd") : "1980-01-01";
    const formattedFirstDue = firstDue ? format(firstDue, "yyyy-MM-dd") : null;
    const normalizedChipNumber = (chipNumber || "").replace(/\D/g, "").slice(0, 15);

    const customerAddress: Address = {
      street: submittedStreet,
      housenumber: submittedHousenumber || undefined,
      zip: submittedZip,
      city: submittedCity,
      countryCode: submittedCountry.toUpperCase().substring(0, 2),
    };

    const customerContactDetails: ContactDetails[] = [];
    if (email) customerContactDetails.push({ value: email, category: "email" });
    if (phone) customerContactDetails.push({ value: phone, category: "cellphone" });

    const customer: Customer = {
      objectType: "NaturalPerson",
      firstname: firstName,
      name: lastName,
      birthdate: formattedCustomerBirthdate,
      email: email,
      gender: effectiveCustomerGender === "male" ? "male" : "female",
      language: currentLocale.toUpperCase() as "DE" | "FR" | "IT" | "EN",
      address: customerAddress,
      contactDetails: customerContactDetails.length > 0 ? customerContactDetails : undefined,
    };

    let finalBreed: string | null = null;
    let isCrossbreedFlag = false;

    if (selectedBreedType === "purebred" || selectedBreedType === "crossbreed") {
      finalBreed = breed;
      isCrossbreedFlag = selectedBreedType === "crossbreed";
    } else if (selectedBreedType === "mixed") {
      isCrossbreedFlag = false;
      if (petType === "cat") finalBreed = "mischlingskatze";
      else if (petType === "dog") finalBreed = selectedDogSize === "small" ? "mischling_klein" : "mischling_gross";
    }

    const funnelKey = readPathParam("k");
    const coverageState: CoverageState = { coverageSelections, subCoverageSelections, subCoverageSumInsured, coverageDeductibles, coverageDeductiblesOtherBand, coverageSumInsured, coverageSumInsuredPerCase, emergencyCareSumInsuredPerDay, annualDeductible };
    const coveragesPayload = buildCoveragesPayload(coverageState, birthDate ?? null);
    if (coveragesPayload.preexistingConditions) {
      coveragesPayload.preexistingConditions.categories = preexistingConditionCategories as PreexistingConditions["categories"];
      if (preexistingConditionCategories.includes("other") && preexistingConditionComment) {
        coveragesPayload.preexistingConditions.categoryComment = preexistingConditionComment;
      }
    }

    return {
      contractID: contractId || undefined,
      contractToken: contractToken || undefined,
      startOfInsurance: formattedStartOfInsurance,
      ...(formattedFirstDue ? { firstDue: formattedFirstDue } : {}),
      ...(campaignCode.trim() ? { campaignCode: campaignCode.trim() } : {}),
      documentDispatch: {
        dispatch: "electronic",
      },
      dispatchControl: buildDispatchControl({
        contract: dispatchContract,
        invoice: dispatchInvoice,
        reminder: dispatchReminder,
        email: dispatchEmail,
        agentEmail: agentDispatchEmail,
        snapshot: offerDispatchSnapshot,
      }),
      dailyTermination: { selected: dailyTermination },
      paymentMethod: paymentMethod,
      yearlyPaymentFrequency: {
        frequency: paymentInterval,
      },
      agentID: agentId || DEFAULT_AGENT_ID,
      ...(whitelabelPartner ? { whitelabelPartner } : {}),
      ...(funnelKey ? { funnelKey } : {}),
      product: {
        productName: "pet",
        ...(premiumCorrections?.length ? { productControl: { premiumCorrections } } : {}),
        applicationQuestions: {
          terminationByInsurer: false,
          rejectedOrSpecialConditions: false,
        },
        contractCoverages: buildContractCoveragesPayload(coverageState, petType),
        pets: [
          {
            species: petType === "dog" ? "Dog" : "Cat",
            name: petName,
            birthdate: formattedPetBirthdate,
            breed: finalBreed,
            crossbreed: isCrossbreedFlag,
            gender: gender,
            castration: isCastrated,
            housecat: petType === "cat" ? isHouseCat : undefined,
            productBundle: selectedProduct ? (selectedProduct.id.toUpperCase() as "S" | "M" | "L") : "L", // Pet 2.0: backend always uses "L" (SIP-4289)
            ...(normalizedChipNumber ? { chipNumber: normalizedChipNumber, chipped: true } : {}),
            coverages: coveragesPayload,
          },
        ],
      },
      customer,
    };
  };

  /** Uploads the Beratungsprotokoll to the (new) contract. The backend does not copy documents when an offer is
   * created from an existing contract, so when nothing new was picked but the previous contract has one, it is
   * read from there (the offer response carries the file) and uploaded again — never kept in the browser. */
  const uploadPendingDocument = async (currentContractId: string, currentContractToken: string) => {
    if (!agentToken) return;
    let file = consultationProtocolPendingFile;
    let filename = consultationProtocolPendingFilename;
    if (!file && consultationProtocolDocumentId && contractId && contractToken && contractId !== currentContractId) {
      try {
        const previousOffer = await getOffer(contractToken, contractId);
        const previous = previousOffer.documents?.find((doc) => doc.documentId === consultationProtocolDocumentId);
        if (!previous?.file) throw new Error("Previous protocol not found on the earlier offer");
        file = previous.file;
        filename = previous.filename ?? null;
      } catch (error) {
        console.error("Carrying over the consultation protocol failed:", error);
        Sentry.captureException(error);
        // The new offer has no protocol, so the UI must not keep claiming it has one.
        clearConsultationProtocolDocument();
        await showErrorDialog(t("consultationProtocol.errors.carryOverFailed"));
        return;
      }
    }
    if (!file) return;
    try {
      const response = await uploadDocument(currentContractToken, currentContractId, {
        file,
        filename: filename || "consultation_protocol",
        kind: "consultation_protocol",
      });
      if (typeof response.documentId === "number") {
        setConsultationProtocolDocument(response.documentId, response.filename || filename || "consultation_protocol");
        clearConsultationProtocolPending();
      } else {
        throw new Error("Missing document ID");
      }
    } catch (error) {
      console.error("Document upload failed:", error);
      Sentry.captureException(error);
      await showErrorDialog(t("consultationProtocol.errors.uploadFailed"));
    }
  };

  const handlePetDetailsSubmitAndFetch = async (
    submittedPetName: string,
    selectedBreed: string,
    submittedBreedLabel: string | null,
    submittedBirthDate: Date | undefined,
    submittedSelectedBreedType: "purebred" | "crossbreed" | "mixed",
    submittedGender: "male" | "female" | "unknown",
    submittedIsCastrated: boolean,
    submittedIsHouseCat: boolean,
    submittedSelectedDogSize: "small" | "large" | null,
    submittedCoverageSelections: Partial<Record<BaseCoverageId, boolean>>,
    submittedStartOfInsurance: Date,
    submittedChipNumber: string | null
  ) => {
    handlePetDetailsSubmitState(
      submittedPetName,
      selectedBreed,
      submittedBreedLabel,
      submittedBirthDate,
      submittedSelectedBreedType,
      submittedGender,
      submittedIsCastrated,
      submittedIsHouseCat,
      submittedSelectedDogSize,
      submittedCoverageSelections,
      submittedStartOfInsurance,
      submittedChipNumber
    );

    if (petType && submittedBirthDate) {
      const recommendations = await fetchBundleRecommendations(
        petType,
        submittedPetName,
        selectedBreed,
        submittedBirthDate,
        submittedSelectedBreedType,
        submittedGender,
        submittedIsCastrated,
        submittedIsHouseCat,
        submittedSelectedDogSize,
        submittedCoverageSelections,
        currentLocale,
        agentId,
        submittedStartOfInsurance
      );

      if (recommendations && !underwritingFailed) {
        setBundleRecommendations(recommendations);
        setBundlePrices({
          S: recommendations.S.grossPremiumProRata ?? 0,
          M: recommendations.M.grossPremiumProRata ?? 0,
          L: recommendations.L.grossPremiumProRata ?? 0,
        });
        navigate(getLocalizedPath('routes.productVariantSelection'));
      }
    }
  };

  /** "auswählen" — picks a tier, stays on the plan table. */
  const handleSelectProductVariant = (product: ProductVariant) => {
    const recommendation = bundleRecommendations?.[product.id.toUpperCase() as "S" | "M" | "L"];
    if (!recommendation) return;
    applyRecommendation(product, recommendation);
  };

  /** "anpassen" (or "Weiter" with a tier already picked) — picks a tier and jumps to step 3. */
  const handleProductVariantSelectionAndNavigate = (product: ProductVariant) => {
    handleSelectProductVariant(product);
    navigate(getLocalizedPath('routes.productCustomization'));
  };

  // PET customers rarely request an offer explicitly, so an offer is created silently
  // when leaving the personal details step, analogous to the KHH funnel.
  const handleContactDetailsSubmitAndCreateOffer = async (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    submittedCity: string,
    submittedZip: string,
    submittedCountry: string,
    submittedBirthdate: Date | undefined,
    submittedGender: "male" | "female" | ""
  ): Promise<boolean> => {
    setIsSubmittingOffer(true);
    // Persist what the customer typed before calling the API, so it survives a failed
    // submission (invalid email, backend error, etc.) instead of being lost the next
    // time the contact details form mounts.
    handleContactDetailsSubmitState(firstName, lastName, email, phone, submittedStreet, submittedHousenumber, submittedCity, submittedZip, submittedCountry, submittedBirthdate, submittedGender);
    try {
      const offerPayload = buildOfferPayload(firstName, lastName, email, phone, submittedStreet, submittedHousenumber, submittedCity, submittedZip, submittedCountry, submittedBirthdate, submittedGender);
      if (!offerPayload) {
        return false;
      }
      const offerResponse = await createOffer(offerPayload);

      if (offerResponse.underwriting === false) {
        reportUnderwritingWarning("offer", offerResponse);
        const reason = offerResponse.underwritingReason || offerResponse.feedbackReason;
        showErrorDialog(reason || t("underwritingError.defaultReason"));
        return false;
      }

      if (offerResponse.grossPremiumProRata === undefined) {
        throw new Error("API did not return grossPremiumProRata for the offer.");
      }

      setFinalPrice(offerResponse.grossPremiumProRata);
      setConfirmedOffer(offerResponse);
      setContractId(offerResponse.contractID || null);
      setContractToken(offerResponse.contractToken || null);
      setOfferLoadedFromUrl(true);
      setHasLeftConfirmationAfterOfferLoad(false);

      if (offerResponse.contractID && offerResponse.contractToken) {
        await uploadPendingDocument(offerResponse.contractID, offerResponse.contractToken);
      }

      return true;
    } catch (err) {
      console.error("Error creating offer:", err);
      if (!isHandledValidationError(err)) Sentry.captureException(err);
      showErrorDialog(getCustomerErrorMessage(err));
      return false;
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const handleCreateApplication = async (
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    submittedStreet: string,
    submittedHousenumber: string,
    submittedCity: string,
    submittedZip: string,
    submittedCountry: string
  ) => {
    setIsSubmittingOffer(true);
    try {
      const shouldReuseExistingOffer = offerLoadedFromUrl && !hasLeftConfirmationAfterOfferLoad && !!contractId && !!contractToken;
      let currentContractId = contractId;
      let currentContractToken = contractToken;

      if (!shouldReuseExistingOffer) {
        const offerPayload = buildOfferPayload(firstName, lastName, email, phone, submittedStreet, submittedHousenumber, submittedCity, submittedZip, submittedCountry);
        if (!offerPayload) {
          setIsSubmittingOffer(false);
          return;
        }
        const offerResponseForApplication = await createOffer(offerPayload);

        if (offerResponseForApplication.underwriting === false) {
          reportUnderwritingWarning("offer", offerResponseForApplication);
          const reason = offerResponseForApplication.underwritingReason || offerResponseForApplication.feedbackReason;
          showErrorDialog(reason || t("underwritingError.defaultReason"));
          return;
        }

        currentContractId = offerResponseForApplication.contractID || null;
        currentContractToken = offerResponseForApplication.contractToken || null;
        // Keep state in sync with the offer we are about to apply
        if (offerResponseForApplication.grossPremiumProRata !== undefined) {
          setFinalPrice(offerResponseForApplication.grossPremiumProRata);
          setConfirmedOffer(offerResponseForApplication);
        } else {
          console.warn("Offer creation did not return grossPremiumProRata; continuing to apply.");
        }
      } else {
        // Reuse the loaded offer without creating a new one; just keep contact details in sync.
      }

      // Keep contact details in sync regardless of the path taken above
      handleContactDetailsSubmitState(firstName, lastName, email, phone, submittedStreet, submittedHousenumber, submittedCity, submittedZip, submittedCountry, contactBirthdate, customerGender || "");

      if (!currentContractId || !currentContractToken) {
        throw new Error("Offer creation failed to return contractID or contractToken.");
      }

      await uploadPendingDocument(currentContractId, currentContractToken);

      const formattedStartOfInsurance = format(startOfInsurance, "yyyy-MM-dd");
      const normalizedChipNumber = (chipNumber || "").replace(/\D/g, "").slice(0, 15);
      const applicationPayload: ApplicationRequest = {
        contractID: currentContractId,
        contractToken: currentContractToken,
        startOfInsurance: formattedStartOfInsurance,
        campaignCode: campaignCode.trim() || undefined,
        chipNumber: normalizedChipNumber || undefined,
      };

      const applicationResponse = await createApplication(applicationPayload);

      if (applicationResponse.underwriting === false) {
        reportUnderwritingWarning("application", applicationResponse);
        const reason = applicationResponse.underwritingReason || applicationResponse.feedbackReason;
        showErrorDialog(reason || t("underwritingError.defaultReason"));
        return;
      }

      if (applicationResponse.grossPremiumProRata !== undefined) {
        setFinalPrice(applicationResponse.grossPremiumProRata);
        handleResetFunnel();
        // Redirect to external thank you page
        window.location.href = t('externalLinks.thankYouPage');
      } else {
        throw new Error("API did not return grossPremiumProRata for the application.");
      }
    } catch (err) {
      console.error("Error creating offer or application:", err);
      if (!isHandledValidationError(err)) Sentry.captureException(err);
      showErrorDialog(getCustomerErrorMessage(err));
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  return {
    handlePetDetailsSubmitAndFetch,
    handleSelectProductVariant,
    handleProductVariantSelectionAndNavigate,
    handleCreateApplication,
    handleContactDetailsSubmitAndCreateOffer,
    isSubmittingOffer,
  };
};
